import axios from 'axios';
import Cookies from 'js-cookie';
import CryptoJS from 'crypto-js';

const SECRET_KEY = 'tabtrips_hotel_secret';
const TOKEN_COOKIE_KEY = 'token';
const PROPERTY_CREATE_URL = 'https://api.tabtrips.com/olivrweb/hotelapi/Api.php/setHotels';
const ROOM_CATEGORY_SEARCH_URL =
  'https://api.tabtrips.com/olivrweb/hotelapi/adminPanel.php?action=searchRoomCategories';
const ADD_ROOM_URL =
  'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php?action=addNewRoomApprovalRequest';
// Same endpoint/form shape the dashboard Property Images page uses.
const HOTEL_IMAGE_UPLOAD_URL = 'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/updateImage';

function getDecryptedToken() {
  const encrypted = Cookies.get(TOKEN_COOKIE_KEY);
  if (!encrypted) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, SECRET_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    return null;
  }
}

function str(value) {
  if (value === undefined || value === null) return '';
  return String(value);
}

// Some backend APIs use PHP empty() checks, so empty strings get rejected.
// For optional fields we send a single space so the field appears "filled".
function nonEmpty(value) {
  const s = value === undefined || value === null ? '' : String(value).trim();
  return s === '' ? ' ' : s;
}

function boolStr(value) {
  return value ? 'Yes' : 'No';
}

function arrStr(value) {
  if (Array.isArray(value)) return value.join(', ');
  if (value === undefined || value === null || value === '') return '';
  return String(value);
}

function buildOccupancyRatesObject(rates, occupancy) {
  const occ = Math.max(1, parseInt(occupancy, 10) || 1);
  const src = rates && typeof rates === 'object' ? rates : {};
  const out = {};
  for (let i = 1; i <= occ; i += 1) {
    const key = `${i}guest`;
    const n = Number(src[key]);
    out[key] = Number.isFinite(n) && n > 0 ? n : 0;
  }
  return out;
}

/** Wizard state carries `rooms: []`; older drafts carried a single room in flat fields. */
export function getRoomsFromState(state) {
  const s = state || {};
  if (Array.isArray(s.rooms)) return s.rooms;
  if (s.roomTypeCategoryId || s.roomTypeName) return [s];
  return [];
}

function buildSingleRoomSummary(room) {
  const r = room || {};
  const parts = [];
  const rates = buildOccupancyRatesObject(r.occupancyRates, r.maxOccupancy);
  const rateBits = Object.entries(rates)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => `${k.replace('guest', '')}G ₹${v}`)
    .join(', ');
  if (r.roomTypeName) {
    parts.push(`${r.roomTypeName}: ${r.roomCount || 1} room(s)${rateBits ? ` (${rateBits})` : ''}`);
  }
  if (r.maxOccupancy) {
    parts.push(`Max ${r.maxOccupancy} guests`);
  }
  if (Array.isArray(r.selectedRoomAmenities) && r.selectedRoomAmenities.length > 0) {
    parts.push(`Amenities: ${r.selectedRoomAmenities.join(', ')}`);
  }
  if (r.rateStartDate || r.rateEndDate) {
    parts.push(`Rates ${r.rateStartDate || '?'} to ${r.rateEndDate || '?'}`);
  }
  return parts.join(' | ');
}

function buildRoomSummary(s) {
  return getRoomsFromState(s)
    .map(buildSingleRoomSummary)
    .filter((part) => part !== '')
    .join(' || ');
}

function totalRoomCount(s) {
  return getRoomsFromState(s).reduce((sum, r) => sum + (parseInt(r.roomCount, 10) || 0), 0);
}

export function buildAddRoomPayload({ token, hotelId, state }) {
  const s = state || {};
  const occupancy = parseInt(s.maxOccupancy, 10) || 1;
  const rates = buildOccupancyRatesObject(s.occupancyRates, occupancy);
  return {
    token,
    hotel_id: parseInt(hotelId, 10),
    room_type_id: parseInt(s.roomTypeCategoryId, 10),
    occupancy,
    amenities: Array.isArray(s.selectedRoomAmenities) ? s.selectedRoomAmenities.join(', ') : '',
    rate: JSON.stringify(rates),
    start_date: str(s.rateStartDate),
    end_date: str(s.rateEndDate),
    room_count: parseInt(s.roomCount, 10) || 1,
  };
}

export async function searchRoomCategories(query) {
  const q = String(query || '').trim();
  if (q.length < 2) return { ok: true, data: [] };
  try {
    const response = await axios.post(
      ROOM_CATEGORY_SEARCH_URL,
      { q },
      { headers: { 'Content-Type': 'application/json' } }
    );
    const data = response.data || {};
    if (data.status && Array.isArray(data.data)) {
      return { ok: true, data: data.data };
    }
    return { ok: false, data: [], message: data.message || 'Search failed' };
  } catch (error) {
    return {
      ok: false,
      data: [],
      message: error.response?.data?.message || error.message || 'Search failed',
    };
  }
}

export async function addRoomTypeForHotel(payload) {
  try {
    const response = await axios.post(ADD_ROOM_URL, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    const data = response.data || {};
    if (data.status) {
      return { ok: true, message: data.message || 'Room type added', data };
    }
    const message = data.message || 'Failed to add room type';
    const lower = String(message).toLowerCase();
    return {
      ok: false,
      message,
      isAuthError: lower.includes('token') || lower.includes('unauthorized') || lower.includes('auth'),
    };
  } catch (error) {
    return {
      ok: false,
      message: error.response?.data?.message || error.message || 'Failed to add room type',
      isAuthError: error.response?.status === 401 || error.response?.status === 403,
    };
  }
}

function buildPropertyPayload(state) {
  const s = state || {};
  const addressParts = [s.address, s.city, s.state, s.country]
    .map((p) => (p === undefined || p === null ? '' : String(p).trim()))
    .filter((p) => p !== '');
  const address = addressParts.join(', ');

  const lat = str(s.latitude);
  const lng = str(s.longitude);
  const locationPoint = lat && lng ? `https://www.google.com/maps?q=${lat},${lng}` : ' ';

  const amenities = arrStr(s.selectedAmenities);
  const hotelPolicy = arrStr(s.selectedHotelPolicies);
  const cancellationPolicy = arrStr(s.selectedCancellationPolicies) || ' ';
  const roomSummary = buildRoomSummary(s) || ' ';

  return {
    hotel_name: nonEmpty(s.propertyName),
    type: nonEmpty(s.selectedPropertyType),
    address,
    city_name: nonEmpty(s.city),
    state_name: nonEmpty(s.state),
    country_name: nonEmpty(s.country),
    pincode: nonEmpty(s.pincode),
    latitude: lat || ' ',
    longitude: lng || ' ',
    description: nonEmpty(s.propertyDescription),
    phone: nonEmpty(s.phoneNumber || s.accountPhone),
    email: nonEmpty(s.email || s.accountEmail),
    check_in_time: nonEmpty(s.checkinTime),
    check_out_time: nonEmpty(s.checkoutTime),
    amenities,
    property_amenities: amenities,
    hotel_policy: hotelPolicy,
    cancellation_policy: cancellationPolicy,
    open_24_7: boolStr(s.isOpen24x7),
    is_couple_allowed: boolStr(s.isCoupleFriendly),
    host_name: nonEmpty(s.fullName),
    host_email: nonEmpty(s.accountEmail),
    host_phone: nonEmpty(s.accountPhone),
    password: nonEmpty(s.password),
    confirm_password: nonEmpty(s.confirmPassword),
    rating: nonEmpty('3'),
    room_count: nonEmpty(String(totalRoomCount(s) || 1)),
    room_summary: roomSummary,
    hotel_percentage: nonEmpty('15'),
    location_point: locationPoint,
    location: locationPoint,
    // 1 = prepaid. Listing and admin listing always create prepaid properties.
    payment_mode: '1',
    pan_number: nonEmpty(s.panNumber),
    gst_number: nonEmpty(s.gstNumber),
    gst_active: String(s.gstActive || '').toLowerCase() === 'active' ? 'active' : undefined,
  };
}

function toFormData(payloadObject) {
  const formData = new FormData();
  const payload = payloadObject || {};
  Object.keys(payload).forEach((key) => {
    const value = payload[key];
    if (value === undefined || value === null) return;
    formData.append(key, String(value));
  });
  return formData;
}

async function createProperty(payloadObject, options = {}) {
  const formData = toFormData(payloadObject);
  const authToken = options?.token;
  if (authToken) {
    formData.append('token', String(authToken));
  }
  try {
    const response = await axios.post(PROPERTY_CREATE_URL, formData);
    const data = response.data || {};
    if (data.status) {
      return {
        ok: true,
        hotel_id: data.hotel_id,
        token: data.token || null,
        token_expiry: data.token_expiry || null,
        message: data.message,
      };
    }
    const message = data.message || 'Failed to create property';
    const lower = String(message).toLowerCase();
    const isAuthError =
      lower.includes('token') ||
      lower.includes('unauthorized') ||
      lower.includes('auth') ||
      lower.includes('login');
    return { ok: false, message, isAuthError };
  } catch (error) {
    if (error.response) {
      return {
        ok: false,
        message: error.response.data?.message || error.message,
        isAuthError: error.response.status === 401 || error.response.status === 403,
      };
    }
    return { ok: false, message: error.message, isAuthError: false };
  }
}

/**
 * Upload wizard images with the dashboard's updateImage API.
 * @param imagesBySection `{ [sectionKey]: File[] }`; each file is appended as `<sectionKey>[]`.
 */
async function uploadHotelImages({ token, hotelId, imagesBySection, onProgress }) {
  const sections = imagesBySection && typeof imagesBySection === 'object' ? imagesBySection : {};
  const formData = new FormData();
  formData.append('hotel_id', String(hotelId));
  formData.append('token', String(token));
  let count = 0;
  Object.entries(sections).forEach(([sectionKey, files]) => {
    (Array.isArray(files) ? files : []).forEach((file) => {
      if (!file) return;
      formData.append(`${sectionKey}[]`, file);
      count += 1;
    });
  });
  if (count === 0) return { ok: true, count: 0, skipped: true };

  try {
    const response = await axios.post(HOTEL_IMAGE_UPLOAD_URL, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (evt) => {
        if (typeof onProgress !== 'function' || !evt.total) return;
        onProgress(Math.round((evt.loaded * 100) / evt.total));
      },
    });
    const data = response.data || {};
    if (data.status) {
      return { ok: true, count, added_images: data.added_images || null, message: data.message };
    }
    const message = data.message || 'Failed to upload images';
    const lower = String(message).toLowerCase();
    return {
      ok: false,
      count,
      message,
      isAuthError: lower.includes('token') || lower.includes('unauthorized'),
    };
  } catch (error) {
    return {
      ok: false,
      count,
      message: error.response?.data?.message || error.message || 'Failed to upload images',
      isAuthError: error.response?.status === 401 || error.response?.status === 403,
    };
  }
}

/** Signup replies for an email that already has a host account. */
export function isAlreadyRegisteredMessage(message) {
  return /already|exist|registered|duplicate/i.test(String(message || ''));
}

export {
  PROPERTY_CREATE_URL,
  ROOM_CATEGORY_SEARCH_URL,
  ADD_ROOM_URL,
  HOTEL_IMAGE_UPLOAD_URL,
  uploadHotelImages,
  getDecryptedToken,
  buildPropertyPayload,
  buildOccupancyRatesObject,
  toFormData,
  createProperty,
};

export async function fetchHostProfileFromToken(token) {
  if (!token) return null;
  try {
    const formData = new FormData();
    formData.append('token', token);
    const response = await axios.post(
      'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/getHostAllHotels',
      formData
    );
    const data = response.data || {};
    if (!data.status) return null;
    const subs = Array.isArray(data.allSubHosts) ? data.allSubHosts : [];
    const owner = subs.find((s) => String(s.role || '').toLowerCase() === 'owner') || subs[0];
    if (!owner) return null;
    return {
      name: owner.name || '',
      email: owner.email || '',
      phone: owner.phone || '',
      host_id: owner.host_id || owner.id,
    };
  } catch {
    return null;
  }
}
